import { useState, useEffect } from 'react'
import { signIn, signUp, signOut } from '../utils/auth'

interface AuthModalProps {
  isOpen: boolean
  onClose: () => void
  initialMode?: 'login' | 'signup'
}

const AuthModal = ({ isOpen, onClose, initialMode = 'login' }: AuthModalProps) => {
  const [isSignUp, setIsSignUp] = useState(initialMode === 'signup')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const [passwordStrength, setPasswordStrength] = useState<'weak' | 'medium' | 'strong' | null>(null)
  const [emailValid, setEmailValid] = useState<boolean | null>(null)

  // Reset form when modal opens/closes or mode changes
  useEffect(() => {
    if (isOpen) {
      setEmail('')
      setPassword('')
      setConfirmPassword('')
      setError(null)
      setSuccess(null)
      setPasswordStrength(null)
      setEmailValid(null)
    }
  }, [isOpen, isSignUp])

  if (!isOpen) return null

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return re.test(email)
  }

  const checkPasswordStrength = (password: string) => {
    if (password.length < 6) return 'weak'
    if (password.length >= 8 && /[A-Z]/.test(password) && /[0-9]/.test(password)) return 'strong'
    return 'medium'
  }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setEmail(value)
    if (value) {
      setEmailValid(validateEmail(value))
    } else {
      setEmailValid(null)
    }
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setPassword(value)
    if (value) {
      setPasswordStrength(checkPasswordStrength(value))
    } else {
      setPasswordStrength(null)
    }
  }

  const getPasswordFeedback = () => {
    if (!password) return null
    
    switch (passwordStrength) {
      case 'weak':
        return { message: 'Password is too short', color: 'text-peach-600' }
      case 'medium':
        return { message: 'Password is acceptable', color: 'text-cream-600' }
      case 'strong':
        return { message: 'Password is strong', color: 'text-mint-600' }
      default:
        return null
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setSuccess(null)

    try {
      if (isSignUp) {
        // Validate passwords match
        if (password !== confirmPassword) {
          throw new Error('Passwords do not match')
        }
        
        // Validate password strength
        if (passwordStrength === 'weak') {
          throw new Error('Please use a stronger password (at least 6 characters)')
        }
        
        await signUp(email, password)
        setSuccess('Your account has been created! Please check your email to confirm your account.')
        
        // Automatically switch to login after signup
        setTimeout(() => {
          setIsSignUp(false)
          setSuccess(null)
        }, 3000)
      } else {
        await signIn(email, password)
        setSuccess('Login successful!')
        setTimeout(() => {
          onClose()
        }, 1000)
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  const passwordFeedback = getPasswordFeedback()

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 sm:p-8">
      <div className={`${isSignUp ? 'bg-mint-50/95' : 'bg-cream-25'} rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-lg transition-all duration-300`}>
        <div className="text-center mb-6">
          <h2 className={`${isSignUp ? 'text-mint-600' : 'text-cream-600'} font-light text-lg sm:text-xl mb-2`}>
            {isSignUp ? 'Create Your Account' : 'Welcome Back'}
          </h2>
          <p className={`${isSignUp ? 'text-mint-500' : 'text-cream-500'} font-light text-xs sm:text-sm`}>
            {isSignUp 
              ? 'Save your progress and access your data anywhere'
              : 'Sign in to access your saved data'
            }
          </p>
        </div>

        {success && (
          <div className="p-3 bg-mint-50/80 border border-mint-100/50 rounded-2xl mb-4">
            <p className="text-mint-600 text-xs sm:text-sm font-light">{success}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className={`block ${isSignUp ? 'text-mint-600' : 'text-cream-600'} font-light text-xs sm:text-sm mb-1`}>
              Email
            </label>
            <div className="relative">
              <input
                id="email"
                type="email"
                placeholder="your.email@example.com"
                value={email}
                onChange={handleEmailChange}
                className={`w-full p-3 sm:p-4 rounded-2xl border ${
                  emailValid === true ? 'border-mint-200/50 focus:border-mint-300' : 
                  emailValid === false ? 'border-peach-200/50 focus:border-peach-300' : 
                  isSignUp ? 'border-mint-100/50 focus:border-mint-200' : 'border-cream-200/50 focus:border-cream-300'
                } focus:ring-2 ${
                  isSignUp ? 'focus:ring-mint-100/50' : 'focus:ring-cream-100/50'
                } focus:outline-none font-light text-sm bg-white/50`}
                required
                aria-invalid={emailValid === false}
                aria-describedby={emailValid === false ? "email-error" : undefined}
              />
              {emailValid !== null && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {emailValid ? (
                    <svg className="w-4 h-4 text-mint-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 text-peach-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                </div>
              )}
            </div>
            {emailValid === false && (
              <p id="email-error" className="mt-1 text-peach-600 text-xs">
                Please enter a valid email address
              </p>
            )}
          </div>
          
          <div>
            <label htmlFor="password" className={`block ${isSignUp ? 'text-mint-600' : 'text-cream-600'} font-light text-xs sm:text-sm mb-1`}>
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder={isSignUp ? "Create a secure password" : "Enter your password"}
                value={password}
                onChange={handlePasswordChange}
                className={`w-full p-3 sm:p-4 pr-10 rounded-2xl border ${
                  passwordStrength === 'strong' ? 'border-mint-200/50 focus:border-mint-300' : 
                  passwordStrength === 'weak' ? 'border-peach-200/50 focus:border-peach-300' : 
                  isSignUp ? 'border-mint-100/50 focus:border-mint-200' : 'border-cream-200/50 focus:border-cream-300'
                } focus:ring-2 ${
                  isSignUp ? 'focus:ring-mint-100/50' : 'focus:ring-cream-100/50'
                } focus:outline-none font-light text-sm bg-white/50`}
                required
                minLength={6}
                aria-describedby={isSignUp ? "password-requirements" : undefined}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-500 focus:outline-none"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            {isSignUp && (
              <div id="password-requirements" className="mt-1 text-xs">
                {passwordFeedback && (
                  <p className={passwordFeedback.color}>{passwordFeedback.message}</p>
                )}
                <p className={`${isSignUp ? 'text-mint-500' : 'text-cream-500'} mt-1`}>
                  Use at least 6 characters. 8+ with numbers and uppercase letters is best.
                </p>
              </div>
            )}
          </div>

          {isSignUp && (
            <div>
              <label htmlFor="confirmPassword" className="block text-mint-600 font-light text-xs sm:text-sm mb-1">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`w-full p-3 sm:p-4 pr-10 rounded-2xl border ${
                    confirmPassword && password === confirmPassword ? 'border-mint-200/50' : 
                    confirmPassword ? 'border-peach-200/50' : 'border-mint-100/50'
                  } focus:ring-2 focus:ring-mint-100/50 focus:outline-none font-light text-sm bg-white/50`}
                  required
                  aria-invalid={confirmPassword && password !== confirmPassword}
                  aria-describedby={confirmPassword && password !== confirmPassword ? "confirm-error" : undefined}
                />
                {confirmPassword && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {password === confirmPassword ? (
                      <svg className="w-4 h-4 text-mint-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-peach-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                  </div>
                )}
              </div>
              {confirmPassword && password !== confirmPassword && (
                <p id="confirm-error" className="mt-1 text-peach-600 text-xs">
                  Passwords don't match
                </p>
              )}
            </div>
          )}

          {error && (
            <div className="p-3 bg-peach-50/60 border border-peach-200/50 rounded-2xl">
              <p className="text-peach-600 text-xs sm:text-sm font-light">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || (isSignUp && password !== confirmPassword) || (isSignUp && passwordStrength === 'weak')}
            className={`w-full px-4 sm:px-6 py-3 sm:py-4 ${
              isSignUp 
                ? 'bg-mint-100/80 hover:bg-mint-200/60 text-mint-600 border-mint-200/50 hover:border-mint-300/60 focus:ring-mint-100'
                : 'bg-cream-100/80 hover:bg-cream-200/60 text-cream-600 border-cream-200/50 hover:border-cream-300/60 focus:ring-cream-100'
            } rounded-2xl text-sm sm:text-base font-light border transition-colors duration-300 focus:outline-none focus:ring-4 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Please wait...
              </span>
            ) : (
              isSignUp ? 'Create Account' : 'Sign In'
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => {
              setIsSignUp(!isSignUp)
              setError(null)
              setSuccess(null)
            }}
            className={`text-${isSignUp ? 'mint' : 'cream'}-500 text-xs sm:text-sm font-light hover:text-${isSignUp ? 'mint' : 'cream'}-600 transition-colors`}
          >
            {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
          </button>
        </div>

        <div className="mt-4 text-center">
          <button
            onClick={onClose}
            className={`text-${isSignUp ? 'mint' : 'cream'}-400 text-xs sm:text-sm font-light hover:text-${isSignUp ? 'mint' : 'cream'}-500 transition-colors`}
          >
            Continue without account
          </button>
        </div>
      </div>
    </div>
  )
}

export default AuthModal